import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  Zap,
  Users,
  MapPin,
} from "lucide-react";
import { FlagUK, FlagAU, FlagNZ } from "@/components/Flags";
import { CommonwealthBackdrop } from "@/components/CommonwealthBackdrop";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_672445ef-2841-43a6-974f-5ce13dca642e/artifacts/mkkzh5ga_e29fbeb6-4e6b-4cb4-b2f9-06ccc88daf69.jpg";

const REGIONS = [
  {
    code: "uk",
    country: "United Kingdom",
    flag: FlagUK,
    href: "https://www.simplyfunded.co.uk",
    external: true,
    currency: "£",
    range: "£3,000 – £200,000",
    tagline: "Fast funding for UK SMEs",
    description:
      "Direct lender for British SMEs. Decisions in hours, funds in your account within 24 hours.",
    cta: "Visit simplyfunded.co.uk",
    badge: "Live",
    badgeTone: "live",
    address: [
      "S10.B Evolve Business Centre,",
      "Cygnet Way, Houghton Le Spring,",
      "England, DH4 5QY",
    ],
  },
  {
    code: "au",
    country: "Australia",
    flag: FlagAU,
    href: "https://simplyfunded.com.au",
    external: true,
    currency: "A$",
    range: "$5,000 – $200,000",
    tagline: "Working capital for Aussie businesses",
    description:
      "Same-day approvals, flexible terms, and all credit profiles considered. Built for Australian SMEs.",
    cta: "Visit simplyfunded.com.au",
    badge: "Live",
    badgeTone: "live",
    address: [
      "22 Greenhill Road, Wayville,",
      "South Australia, 5034,",
      "Australia",
    ],
  },
  {
    code: "nz",
    country: "New Zealand",
    flag: FlagNZ,
    href: "/coming-soon",
    external: false,
    currency: "NZ$",
    range: null,
    tagline: "Kiwi businesses, coming soon",
    description:
      "We're bringing fast, transparent business funding to New Zealand. Be the first to know when we launch.",
    cta: "Get launch updates",
    badge: "Coming Q4 2026",
    badgeTone: "soon",
    address: null,
  },
];

const TRUST_POINTS = [
  {
    icon: Zap,
    title: "Decisions in hours",
    body: "60-second application. Funds released as soon as the same day.",
  },
  {
    icon: ShieldCheck,
    title: "100% transparent",
    body: "Fixed pricing, no hidden fees, no surprises. What you see is what you pay.",
  },
  {
    icon: Users,
    title: "Dedicated support",
    body: "Local teams who understand SME realities — not call-centre scripts.",
  },
];

export default function Landing() {
  const year = new Date().getFullYear();

  return (
    <main
      data-testid="landing-page"
      className="relative min-h-screen overflow-hidden bg-white text-[color:var(--sf-ink)]"
    >
      {/* Background grid + commonwealth unity arcs */}
      <div className="pointer-events-none absolute inset-0 bg-grid bg-grid-fade" />
      <CommonwealthBackdrop />

      {/* Top bar */}
      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 sm:px-10 sm:py-8">
        <div data-testid="header-logo" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="SimplyFunded by Morley Capital"
            className="h-16 w-auto sm:h-20 lg:h-24"
            draggable={false}
          />
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pt-10 sm:px-10 sm:pt-16">
        <div className="max-w-3xl">
          <div
            data-testid="hero-eyebrow"
            className="rise inline-flex items-center gap-2 rounded-full border border-[color:var(--sf-line)] bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--sf-blue)] backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[color:var(--sf-blue)] opacity-60 pulse-dot" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--sf-blue)]" />
            </span>
            Now lending in UK &amp; Australia
          </div>

          <h1
            data-testid="hero-title"
            className="font-display rise mt-6 text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "0.05s" }}
          >
            Choose your region.
            <br />
            <span className="text-[color:var(--sf-blue)]">Get funded, simply.</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="rise mt-6 max-w-2xl text-base text-[color:var(--sf-mute)] sm:text-lg"
            style={{ animationDelay: "0.12s" }}
          >
            SimplyFunded is a direct lender providing fast, flexible short‑term business
            loans to SMEs. Pick the country your business operates in to continue.
          </p>
        </div>
      </section>

      {/* Tiles */}
      <section
        data-testid="tiles-section"
        className="relative z-10 mx-auto mt-14 grid max-w-7xl gap-6 px-6 sm:mt-20 sm:px-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-7"
      >
        {REGIONS.map((r, idx) => {
          const Inner = (
            <article
              className="tile-card relative flex h-full flex-col overflow-hidden rounded-3xl border border-[color:var(--sf-line)] bg-white p-7 sm:p-8"
              data-testid={`tile-${r.code}-card`}
            >
              {/* Blue stripe */}
              <span className="tile-stripe absolute left-0 top-0 h-1 w-12 bg-[color:var(--sf-blue)]" />

              {/* Top row */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-[color:var(--sf-line)] bg-[color:var(--sf-blue-soft)]">
                  <r.flag className="h-9 w-9" />
                </div>
                <span
                  data-testid={`tile-${r.code}-badge`}
                  className={
                    r.badgeTone === "live"
                      ? "inline-flex items-center gap-1.5 rounded-full bg-[color:var(--sf-blue-soft)] px-3 py-1 text-xs font-semibold text-[color:var(--sf-blue-deep)]"
                      : "inline-flex items-center gap-1.5 rounded-full border border-dashed border-[color:var(--sf-blue)] px-3 py-1 text-xs font-semibold text-[color:var(--sf-blue)]"
                  }
                >
                  {r.badgeTone === "live" && (
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--sf-blue)] pulse-dot" />
                  )}
                  {r.badge}
                </span>
              </div>

              {/* Body */}
              <div className="mt-7">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--sf-mute)]">
                  {r.tagline}
                </p>
                <h3
                  data-testid={`tile-${r.code}-country`}
                  className="font-display mt-2 text-2xl font-bold sm:text-[28px]"
                >
                  {r.country}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--sf-mute)]">
                  {r.description}
                </p>
              </div>

              {/* Loan size chip */}
              {r.range && (
                <div className="mt-6 inline-flex w-fit items-baseline gap-2 rounded-xl bg-[color:var(--sf-ink)] px-3.5 py-2 text-white">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-white/60">
                    Loan size
                  </span>
                  <span className="font-display text-sm font-semibold">{r.range}</span>
                </div>
              )}

              {/* Address */}
              {r.address && (
                <div
                  data-testid={`tile-${r.code}-address`}
                  className="mt-6 flex items-start gap-2.5 border-t border-[color:var(--sf-line)] pt-5"
                >
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--sf-blue)]" />
                  <address className="text-xs not-italic leading-relaxed text-[color:var(--sf-mute)]">
                    {r.address.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </address>
                </div>
              )}

              {/* CTA */}
              <div className="mt-auto flex items-end justify-between pt-8">
                <span
                  data-testid={`tile-${r.code}-cta-label`}
                  className="text-sm font-semibold text-[color:var(--sf-ink)] group-hover:text-[color:var(--sf-blue)]"
                >
                  {r.cta}
                </span>
                <span
                  className="tile-arrow inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--sf-blue-soft)] text-[color:var(--sf-blue)]"
                  aria-hidden
                >
                  <ArrowUpRight className="h-5 w-5" />
                </span>
              </div>
            </article>
          );

          const wrapperClass = "group block rise h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sf-blue)] focus-visible:ring-offset-2 rounded-3xl";
          const delay = { animationDelay: `${0.18 + idx * 0.08}s` };

          return r.external ? (
            <a
              key={r.code}
              data-testid={`tile-${r.code}-link`}
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              className={wrapperClass}
              style={delay}
            >
              {Inner}
            </a>
          ) : (
            <Link
              key={r.code}
              data-testid={`tile-${r.code}-link`}
              to={r.href}
              className={wrapperClass}
              style={delay}
            >
              {Inner}
            </Link>
          );
        })}
      </section>

      {/* Trust strip */}
      <section
        data-testid="trust-section"
        className="relative z-10 mx-auto mt-24 max-w-7xl px-6 sm:px-10"
      >
        <div className="rounded-3xl border border-[color:var(--sf-line)] bg-[color:var(--sf-ink)] p-8 text-white sm:p-12">
          <div className="grid gap-10 md:grid-cols-3 md:gap-12">
            {TRUST_POINTS.map((t) => (
              <div key={t.title} data-testid={`trust-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--sf-blue)]">
                  <t.icon className="h-5 w-5 text-white" />
                </div>
                <h4 className="font-display mt-5 text-xl font-semibold">{t.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        data-testid="footer"
        className="relative z-10 mx-auto mt-20 max-w-7xl px-6 pb-12 sm:px-10"
      >
        <div className="flex flex-col items-start justify-between gap-6 border-t border-[color:var(--sf-line)] pt-8 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="SimplyFunded"
              className="h-8 w-auto opacity-80"
              draggable={false}
            />
            <span className="text-xs text-[color:var(--sf-mute)]">
              SimplyFunded is a trading brand of Morley Capital.
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[color:var(--sf-mute)]">
            <a
              data-testid="footer-uk-link"
              href="https://www.simplyfunded.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[color:var(--sf-blue)]"
            >
              UK
            </a>
            <a
              data-testid="footer-au-link"
              href="https://simplyfunded.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[color:var(--sf-blue)]"
            >
              AU
            </a>
            <Link
              data-testid="footer-nz-link"
              to="/coming-soon"
              className="hover:text-[color:var(--sf-blue)]"
            >
              NZ
            </Link>
            <span>© {year} Morley Capital</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
